import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye, Undo2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspaceStore } from '@/store/workspace.store';
import { useToast } from '@/hooks/useToast';
import { roleLabel } from '@/lib/badges';
import { cuentasParaVerComo, entrarComo, viendoComo, volverAMiCuenta } from '@/lib/ver-como';

/** En el menú de la cuenta: las otras cuentas del negocio, para entrar sin cerrar la sesión. */
export function MenuVerComo() {
  const { user } = useAuth();
  const toast = useToast();
  const [entrando, setEntrando] = useState<string | null>(null);
  const esAdministracion = user?.role === 'OWNER' && !viendoComo();
  const negocio = useWorkspaceStore((s) => s.activeId);
  const { data } = useQuery({
    queryKey: ['ver-como', 'cuentas', negocio],
    queryFn: cuentasParaVerComo,
    enabled: esAdministracion,
  });
  if (!esAdministracion || !data?.length) return null;
  return (
    <div className="mb-1 border-b border-surface-border pb-1 dark:border-white/10">
      <p className="px-[9px] pb-1 pt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">Entrar como</p>
      <div className="max-h-64 overflow-auto">
        {data.map((c) => (
          <button
            key={c.id}
            type="button"
            disabled={entrando !== null}
            onClick={() => {
              setEntrando(c.id);
              entrarComo(c.id).catch((e: unknown) => {
                setEntrando(null);
                toast.error(e instanceof Error ? e.message : 'No se pudo entrar a esa cuenta');
              });
            }}
            className="mac-menu-fila group"
          >
            <Eye className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">{c.nombre}</span>
            <span className="shrink-0 text-xs text-slate-400 group-hover:text-white/80">{roleLabel[c.rol as keyof typeof roleLabel] ?? c.rol}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Arriba de todo mientras se mira otra cuenta: de quién es y cómo volver. */
export function FranjaVerComo() {
  const { user } = useAuth();
  if (!viendoComo()) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-amber-400 px-4 py-2 text-center text-sm font-medium text-amber-950">
      <span>
        Estás viendo como <strong>{user?.name ?? user?.email}</strong>. Lo que escribas queda con tu nombre.
      </span>
      <button
        type="button"
        onClick={() => volverAMiCuenta()}
        className="inline-flex items-center gap-1 rounded-full bg-amber-950 px-3 py-1 text-xs font-semibold text-amber-50 hover:bg-amber-900"
      >
        <Undo2 className="h-3.5 w-3.5" />
        Volver a mi cuenta
      </button>
    </div>
  );
}
